import type { z } from 'zod';
import {
  accountAnimalDetailSchema,
  accountAnimalTodaySchema,
  accountAnimalActivitySchema,
  accountAnimalDailySchema,
  accountAnimalTrackSchema,
  accountAnimalSchema,
  accountExplorerSchema,
  accountExplorerCreationPreviewSchema,
  deleteAccountExplorerRequestSchema,
  accountRegistrationResponseSchema,
  accountSchema,
  accountDeletionProgressSchema,
  currentPolicySchema,
  adminAccessCodeSchema,
  adminAuditLogSchema,
  adminCatalogSchema,
  adminJobRunSchema,
  adminOverviewSchema,
  adminPaginatedSchema,
  adminSessionSchema,
  adminUserSchema,
  advanceResultSchema,
  animalSummarySchema,
  animalTrackSchema,
  apiErrorSchema,
  genericAcceptedSchema,
  passwordResetResponseSchema,
  quizAttemptResultSchema,
  contentListResponseSchema,
  contentRecordSchema,
  dailyGenerationResultSchema,
  profileSchema,
  profileSessionSchema,
  redeemAccountAnimalResponseSchema,
  registrationIntentResponseSchema,
  resendVerificationResponseSchema,
  storefrontCatalogSchema,
  storefrontProductSchema,
  updateAccountRequestSchema,
  updateAccountExplorerRequestSchema,
} from '../../shared/schemas/api';
import type {
  Account,
  AccountAnimal,
  AccountAnimalDetail,
  AccountAnimalActivity,
  AccountAnimalDaily,
  AccountAnimalTrack,
  AccountAnimalToday,
  AccountExplorer,
  AccountExplorerCreationPreview,
  AdminAccessCode,
  AdminAuditLog,
  AdminCatalog,
  AdminJobRun,
  AdminOverview,
  AdminRole,
  AdminSession,
  AdminUser,
  AdvanceResult,
  AnimalSummary,
  AnimalTrack,
  Profile,
  ProfileSession,
  QuizAttemptResult,
  ContentRecord,
  ContentType,
  CurrentPolicy,
  StorefrontCatalog,
  StorefrontProduct,
} from '../../shared/types/api';
import {
  CURRENT_PUBLIC_POLICY_IDENTITY,
  matchesCurrentPublicPolicy,
} from '../content/publicPolicies';

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestInit = {},
  profileId?: string,
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('accept', 'application/json');
  if (options.body) headers.set('content-type', 'application/json');
  if (profileId) headers.set('X-Profile-Id', profileId);
  if (options.method && options.method !== 'GET') {
    const csrf = document.cookie
      .split('; ')
      .find((part) => part.startsWith('wt_account_csrf='))
      ?.slice('wt_account_csrf='.length);
    if (csrf) headers.set('x-csrf-token', decodeURIComponent(csrf));
  }
  const response = await fetch(path, { ...options, headers, credentials: 'same-origin' });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsedError = apiErrorSchema.safeParse(body);
    throw new ApiClientError(
      parsedError.success ? parsedError.data.error.message : 'Wild Trails could not load that yet.',
      parsedError.success ? parsedError.data.error.code : 'request_failed',
      response.status,
    );
  }
  return schema.parse(body);
}

async function downloadAccountData(password: string): Promise<Blob> {
  const csrf = document.cookie
    .split('; ')
    .find((part) => part.startsWith('wt_account_csrf='))
    ?.slice('wt_account_csrf='.length);
  const headers = new Headers({ accept: 'application/json', 'content-type': 'application/json' });
  if (csrf) headers.set('x-csrf-token', decodeURIComponent(csrf));
  const response = await fetch('/api/v1/account/privacy/export', {
    method: 'POST',
    body: JSON.stringify({ password }),
    headers,
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const parsedError = apiErrorSchema.safeParse(body);
    throw new ApiClientError(
      parsedError.success
        ? parsedError.data.error.message
        : 'The account export was not available.',
      parsedError.success ? parsedError.data.error.code : 'request_failed',
      response.status,
    );
  }
  return response.blob();
}

async function getVerifiedCurrentPolicy(path: string): Promise<CurrentPolicy> {
  const policy = await request(path, currentPolicySchema);
  if (!matchesCurrentPublicPolicy(policy)) {
    throw new ApiClientError(
      'The current policies changed. Refresh before continuing.',
      'policy_identity_mismatch',
      409,
    );
  }
  return policy;
}

export const api = {
  createProfile: (): Promise<ProfileSession> =>
    request('/api/v1/profiles', profileSessionSchema, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  recoverProfile: (recoveryCode: string): Promise<ProfileSession> =>
    request('/api/v1/profiles/recover', profileSessionSchema, {
      method: 'POST',
      body: JSON.stringify({ recoveryCode }),
    }),
  getProfile: (profileId: string): Promise<Profile> =>
    request('/api/v1/profile', profileSchema, {}, profileId),
  listAnimals: (profileId: string): Promise<AnimalSummary[]> =>
    request('/api/v1/animals', animalSummarySchema.array(), {}, profileId),
  unlockAnimal: (profileId: string, animalId: string): Promise<AnimalSummary> =>
    request(
      `/api/v1/animals/${animalId}/unlock`,
      animalSummarySchema,
      { method: 'POST' },
      profileId,
    ),
  getTrack: (profileId: string, animalId: string): Promise<AnimalTrack> =>
    request(`/api/v1/animals/${animalId}/track`, animalTrackSchema, {}, profileId),
  advanceTrack: (profileId: string, animalId: string): Promise<AdvanceResult> =>
    request(
      `/api/v1/animals/${animalId}/advance`,
      advanceResultSchema,
      { method: 'POST', body: JSON.stringify({}) },
      profileId,
    ),
  createRegistrationIntent: async (code: string) => {
    const intent = await request(
      '/api/v1/auth/registration-intents',
      registrationIntentResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      },
    );
    if (
      intent.policyVersion &&
      !matchesCurrentPublicPolicy({
        version: intent.policyVersion,
        effectiveAt: intent.policyEffectiveAt!,
        termsSha256: intent.termsSha256!,
        privacySha256: intent.privacySha256!,
      })
    ) {
      throw new ApiClientError(
        'The current policies changed. Refresh before creating an account.',
        'policy_identity_mismatch',
        409,
      );
    }
    return intent;
  },
  registerAccount: (input: {
    registrationToken: string;
    firstName: string;
    email: string;
    password: string;
    policyVersion?: string;
    termsSha256?: string;
    privacySha256?: string;
  }): Promise<{
    accepted: true;
    verificationRequired: true;
    verificationEmailQueued?: boolean;
    developmentVerificationToken?: string;
  }> =>
    request('/api/v1/auth/register', accountRegistrationResponseSchema, {
      method: 'POST',
      body: JSON.stringify({
        ...input,
        adultConfirmed: true,
        ...(input.policyVersion ? { policyAccepted: true } : {}),
      }),
    }),
  verifyAccountEmail: (token: string): Promise<{ accepted: true }> =>
    request('/api/v1/auth/verify-email', genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  loginAccount: (email: string, password: string): Promise<Account> =>
    request('/api/v1/auth/login', accountSchema, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  resendAccountVerification: (
    email: string,
  ): Promise<{ accepted: true; developmentVerificationToken?: string }> =>
    request('/api/v1/auth/resend-verification', resendVerificationResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  requestAccountPasswordReset: (
    email: string,
  ): Promise<{ accepted: true; developmentResetToken?: string }> =>
    request('/api/v1/auth/password-reset/request', passwordResetResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  completeAccountPasswordReset: (token: string, password: string): Promise<{ accepted: true }> =>
    request('/api/v1/auth/password-reset/complete', genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  getAccount: (): Promise<Account> => request('/api/v1/account', accountSchema),
  getCurrentPolicy: (): Promise<CurrentPolicy> =>
    getVerifiedCurrentPolicy('/api/v1/policies/current'),
  getAccountPolicy: (): Promise<CurrentPolicy> =>
    getVerifiedCurrentPolicy('/api/v1/account/policy'),
  acceptCurrentPolicy: (): Promise<{ accepted: true }> =>
    request('/api/v1/account/policy/accept', genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({
        policyVersion: CURRENT_PUBLIC_POLICY_IDENTITY.version,
        termsSha256: CURRENT_PUBLIC_POLICY_IDENTITY.termsSha256,
        privacySha256: CURRENT_PUBLIC_POLICY_IDENTITY.privacySha256,
        accepted: true,
      }),
    }),
  downloadAccountData,
  updateAccount: (input: {
    firstName?: string;
    distanceUnit?: Account['distanceUnit'];
  }): Promise<Account> =>
    request('/api/v1/account', accountSchema, {
      method: 'PATCH',
      body: JSON.stringify(updateAccountRequestSchema.parse(input)),
    }),
  changeAccountPassword: (
    currentPassword: string,
    newPassword: string,
  ): Promise<{ accepted: true }> =>
    request('/api/v1/account/password', genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  requestAccountDeletion: (password: string, confirmation: string): Promise<{ accepted: true }> =>
    request('/api/v1/account/deletion-request', genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({ password, confirmation }),
    }),
  listAccountAnimals: (): Promise<AccountAnimal[]> =>
    request('/api/v1/account/animals', accountAnimalSchema.array()),
  listAccountExplorers: (): Promise<AccountExplorer[]> =>
    request('/api/v1/account/explorers', accountExplorerSchema.array()),
  getAccountExplorerCreationPreview: (): Promise<AccountExplorerCreationPreview> =>
    request('/api/v1/account/explorers/creation-preview', accountExplorerCreationPreviewSchema),
  createAccountExplorer: (input: {
    nickname: string;
    badge: AccountExplorer['badge'];
    idempotencyKey: string;
  }): Promise<AccountExplorer> =>
    request('/api/v1/account/explorers', accountExplorerSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateAccountExplorer: (
    explorerId: string,
    input: { nickname: string; badge: AccountExplorer['badge'] },
  ): Promise<AccountExplorer> =>
    request(`/api/v1/account/explorers/${explorerId}`, accountExplorerSchema, {
      method: 'PATCH',
      body: JSON.stringify(updateAccountExplorerRequestSchema.parse(input)),
    }),
  deleteAccountExplorer: (explorerId: string): Promise<{ accepted: true }> =>
    request(`/api/v1/account/explorers/${explorerId}`, genericAcceptedSchema, {
      method: 'DELETE',
      body: JSON.stringify(deleteAccountExplorerRequestSchema.parse({ confirmation: 'DELETE' })),
    }),
  getAccountAnimal: (entitlementId: string): Promise<AccountAnimalDetail> =>
    request(`/api/v1/account/animals/${entitlementId}`, accountAnimalDetailSchema),
  getAccountAnimalTrack: (entitlementId: string, days = 15): Promise<AccountAnimalTrack> =>
    request(
      `/api/v1/account/animals/${entitlementId}/track?days=${days}`,
      accountAnimalTrackSchema,
    ),
  getAccountAnimalActivity: (entitlementId: string, days = 15): Promise<AccountAnimalActivity> =>
    request(
      `/api/v1/account/animals/${entitlementId}/activity?days=${days}`,
      accountAnimalActivitySchema,
    ),
  getAccountAnimalDay: (entitlementId: string, date: string): Promise<AccountAnimalDaily> =>
    request(`/api/v1/account/animals/${entitlementId}/daily/${date}`, accountAnimalDailySchema),
  getAccountAnimalToday: (entitlementId: string): Promise<AccountAnimalToday> =>
    request(`/api/v1/account/animals/${entitlementId}/today`, accountAnimalTodaySchema),
  submitAccountAnimalQuiz: (
    entitlementId: string,
    date: string,
    answers: Array<{ questionId: string; choiceId: string }>,
  ): Promise<QuizAttemptResult> =>
    request(
      `/api/v1/account/animals/${entitlementId}/quiz/${date}/attempts`,
      quizAttemptResultSchema,
      {
        method: 'POST',
        body: JSON.stringify({ answers }),
      },
    ),
  redeemAccountAnimal: (
    code: string,
    explorerId: string,
  ): Promise<{
    status: 'unlocked' | 'already_unlocked';
    entitlementId: string;
    simulatedAnimalId: string;
  }> =>
    request('/api/v1/account/animals/redeem', redeemAccountAnimalResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ code, explorerId }),
    }),
  logoutAccount: (): Promise<{ accepted: true }> =>
    request('/api/v1/auth/logout', genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  listStorefrontProducts: (): Promise<StorefrontCatalog> =>
    request('/api/v1/catalog/products', storefrontCatalogSchema),
  getStorefrontProduct: (slug: string): Promise<StorefrontProduct> =>
    request(`/api/v1/catalog/products/${encodeURIComponent(slug)}`, storefrontProductSchema),
  getAdminSession: (): Promise<AdminSession> =>
    request('/api/v1/admin/session', adminSessionSchema),
  reauthenticateAdmin: (password: string): Promise<{ accepted: true }> =>
    request('/api/v1/admin/reauthenticate', genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  getAdminOverview: (): Promise<AdminOverview> =>
    request('/api/v1/admin/overview', adminOverviewSchema),
  listAdminAccessCodes: (): Promise<Paginated<AdminAccessCode>> =>
    request('/api/v1/admin/access-codes?pageSize=50', adminPaginatedSchema(adminAccessCodeSchema)),
  listAdminUsers: (search = ''): Promise<Paginated<AdminUser>> =>
    request(
      `/api/v1/admin/users?pageSize=50&search=${encodeURIComponent(search)}`,
      adminPaginatedSchema(adminUserSchema),
    ),
  listAdminJobs: (page = 1, pageSize = 15): Promise<Paginated<AdminJobRun>> =>
    request(
      `/api/v1/admin/jobs?page=${page}&pageSize=${pageSize}`,
      adminPaginatedSchema(adminJobRunSchema),
    ),
  listAdminAudits: (page = 1, pageSize = 15): Promise<Paginated<AdminAuditLog>> =>
    request(
      `/api/v1/admin/audit-logs?page=${page}&pageSize=${pageSize}`,
      adminPaginatedSchema(adminAuditLogSchema),
    ),
  getAdminCatalog: (): Promise<AdminCatalog> =>
    request('/api/v1/admin/catalog', adminCatalogSchema),
  updateAdminProduct: (
    product: AdminCatalog['products'][number],
    reason: string,
  ): Promise<{ accepted: true }> =>
    request(`/api/v1/admin/products/${product.id}`, genericAcceptedSchema, {
      method: 'PUT',
      body: JSON.stringify({ ...product, confirmation: 'CONFIRM', reason }),
    }),
  updateAdminAnimalStatus: (
    id: string,
    status: string,
    reason: string,
  ): Promise<{ accepted: true }> =>
    request(`/api/v1/admin/simulated-animals/${id}/status`, genericAcceptedSchema, {
      method: 'PATCH',
      body: JSON.stringify({ status, confirmation: 'CONFIRM', reason }),
    }),
  updateAdminProductImage: (
    id: string,
    altText: string,
    sortOrder: number,
    reason: string,
  ): Promise<{ accepted: true }> =>
    request(`/api/v1/admin/product-images/${id}`, genericAcceptedSchema, {
      method: 'PUT',
      body: JSON.stringify({ altText, sortOrder, confirmation: 'CONFIRM', reason }),
    }),
  updateAdminSpecies: (
    species: AdminCatalog['species'][number],
    reason: string,
  ): Promise<{ accepted: true }> =>
    request(`/api/v1/admin/species/${species.id}`, genericAcceptedSchema, {
      method: 'PUT',
      body: JSON.stringify({ ...species, confirmation: 'CONFIRM', reason }),
    }),
  setAdminAccessCodeState: (
    id: string,
    active: boolean,
    expiresAt: string | null,
    reason: string,
  ): Promise<{ accepted: true }> =>
    request(`/api/v1/admin/access-codes/${id}`, genericAcceptedSchema, {
      method: 'PATCH',
      body: JSON.stringify({ confirmation: 'CONFIRM', reason, active, expiresAt }),
    }),
  transitionAdminEntitlement: (
    id: string,
    action: 'revoke' | 'reactivate',
    reason: string,
  ): Promise<{ accepted: true }> =>
    request(`/api/v1/admin/entitlements/${id}/${action}`, genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({ confirmation: 'CONFIRM', reason }),
    }),
  disableAdminUser: (id: string, reason: string): Promise<{ accepted: true }> =>
    request(`/api/v1/admin/users/${id}/disable`, genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({ confirmation: 'CONFIRM', reason }),
    }),
  changeAdminUserRole: (
    id: string,
    role: AdminRole,
    action: 'grant' | 'revoke',
    reason: string,
  ): Promise<{ accepted: true }> =>
    request(`/api/v1/admin/users/${id}/roles/${action}`, genericAcceptedSchema, {
      method: 'POST',
      body: JSON.stringify({ confirmation: 'CONFIRM', reason, role }),
    }),
  completeAdminAccountDeletion: (
    id: string,
    reason: string,
  ): Promise<z.infer<typeof accountDeletionProgressSchema>> =>
    request(`/api/v1/admin/users/${id}/delete`, accountDeletionProgressSchema, {
      method: 'POST',
      body: JSON.stringify({ confirmation: 'CONFIRM', reason }),
    }),
  retryAdminJob: (
    localDate: string,
    reason: string,
  ): Promise<z.infer<typeof dailyGenerationResultSchema>> =>
    request(`/api/v1/admin/jobs/retry`, dailyGenerationResultSchema, {
      method: 'POST',
      body: JSON.stringify({ confirmation: 'CONFIRM', reason, localDate }),
    }),
  listAdminContent: (type: ContentType): Promise<Paginated<ContentRecord>> =>
    request(`/api/v1/admin/content/${type}?pageSize=50`, contentListResponseSchema),
  createAdminContent: (type: ContentType, data: Record<string, unknown>): Promise<ContentRecord> =>
    request(`/api/v1/admin/content/${type}`, contentRecordSchema, {
      method: 'POST',
      body: JSON.stringify({ data }),
    }),
  updateAdminContent: (
    type: ContentType,
    id: string,
    data: Record<string, unknown>,
  ): Promise<ContentRecord> =>
    request(`/api/v1/admin/content/${type}/${id}`, contentRecordSchema, {
      method: 'PUT',
      body: JSON.stringify({ data }),
    }),
  setAdminContentActive: (type: ContentType, id: string, active: boolean): Promise<ContentRecord> =>
    request(
      `/api/v1/admin/content/${type}/${id}/${active ? 'activate' : 'deactivate'}`,
      contentRecordSchema,
      { method: 'POST', body: JSON.stringify({}) },
    ),
};
